import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, MessageCircle, User, Clock, Reply, Send, Search, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeacherDiscussions() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [discussions, setDiscussions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({ title: '', content: '', course_id: '' });
  const [replyContent, setReplyContent] = useState('');

  useEffect(() => { fetchData(); }, []);

  const getHeaders = () => {
    const token = localStorage.getItem('staffToken');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) { navigate('/staff-portal'); return; }
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [discussionsRes, coursesRes] = await Promise.all([
        fetch(`${API_URL}/api/lms/discussions`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/api/lms/teacher/courses`, { headers }).then(r => r.json()).catch(() => [])
      ]);

      setDiscussions(Array.isArray(discussionsRes) ? discussionsRes : []);
      setCourses(Array.isArray(coursesRes) ? coursesRes : []);
    } catch (error) {
      toast.error('Failed to load discussions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      toast.error('Please fill required fields');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/lms/discussions`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Discussion topic created!');
        setShowModal(false);
        setFormData({ title: '', content: '', course_id: '' });
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to create');
      }
    } catch (error) {
      toast.error('Failed to create discussion');
    } finally {
      setSaving(false);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim() || !selectedDiscussion) {
      toast.error('Please enter a reply');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/lms/discussions/${selectedDiscussion.id}/replies`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ content: replyContent })
      });

      if (response.ok) {
        toast.success('Reply posted!');
        setShowReplyModal(false);
        setReplyContent('');
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to reply');
      }
    } catch (error) {
      toast.error('Failed to post reply');
    } finally {
      setSaving(false);
    }
  };

  const openReply = (discussion) => {
    setSelectedDiscussion(discussion);
    setShowReplyModal(true);
  };

  const filteredDiscussions = discussions.filter(d => 
    d.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/staff-portal/lms" className="p-2 hover:bg-indigo-600 rounded-lg"><ArrowLeft className="h-5 w-5" /></Link>
              <div><h1 className="text-xl font-bold">Discussion Forum</h1><p className="text-indigo-200 text-sm">Engage with students in discussions</p></div>
            </div>
            <Button onClick={() => setShowModal(true)} className="bg-white text-indigo-700 hover:bg-indigo-50"><Plus className="h-4 w-4 mr-2" />New Topic</Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input placeholder="Search discussions..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>

        {filteredDiscussions.length === 0 ? (
          <Card><CardContent className="p-12 text-center">
            <MessageCircle className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No discussions yet. Start a new topic!</p>
          </CardContent></Card>
        ) : (
          filteredDiscussions.map(discussion => (
            <Card key={discussion.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-teal-100 rounded-lg">
                    <MessageCircle className="h-6 w-6 text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">{discussion.title}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{discussion.content}</p>
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                      <span className="flex items-center gap-1"><User className="h-3 w-3" />{discussion.author_name || 'Teacher'}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{discussion.created_at?.split('T')[0]}</span>
                      <span className="flex items-center gap-1"><Reply className="h-3 w-3" />{discussion.replies?.length || 0} replies</span>
                      {discussion.course_name && <span className="bg-gray-100 px-2 py-0.5 rounded">{discussion.course_name}</span>}
                    </div>
                    
                    {/* Replies Preview */}
                    {discussion.replies?.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-3 mb-3 space-y-2">
                        {discussion.replies.slice(0, 2).map((reply, idx) => (
                          <div key={idx} className="text-sm">
                            <span className="font-medium text-gray-700">{reply.author_name}:</span>
                            <span className="text-gray-600 ml-2">{reply.content.slice(0, 100)}...</span>
                          </div>
                        ))}
                        {discussion.replies.length > 2 && (
                          <p className="text-xs text-gray-500">+ {discussion.replies.length - 2} more replies</p>
                        )}
                      </div>
                    )}
                    
                    <Button variant="outline" size="sm" onClick={() => openReply(discussion)}>
                      <Reply className="h-4 w-4 mr-1" />
                      Reply
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </main>

      {/* New Topic Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-lg">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Start New Discussion</h2>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div><Label>Topic Title *</Label><Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} required /></div>
              
              <div><Label>Content *</Label><textarea value={formData.content} onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" rows={4} required /></div>
              
              <div><Label>Course (Optional)</Label>
                <Select value={formData.course_id} onValueChange={(v) => setFormData(prev => ({ ...prev, course_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="All Students" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Students</SelectItem>
                    {courses.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button>
                <Button type="submit" disabled={saving} className="flex-1">{saving ? 'Creating...' : 'Create Topic'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && selectedDiscussion && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-lg">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Reply to Discussion</h2>
              <button onClick={() => setShowReplyModal(false)}><X className="h-5 w-5" /></button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium text-gray-900">{selectedDiscussion.title}</p>
                <p className="text-sm text-gray-600 mt-1">{selectedDiscussion.content.slice(0, 150)}...</p>
              </div>
              
              <div>
                <Label>Your Reply</Label>
                <textarea value={replyContent} onChange={(e) => setReplyContent(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" rows={4} placeholder="Write your reply..." />
              </div>
              
              <div className="flex gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowReplyModal(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleReply} disabled={saving} className="flex-1">{saving ? 'Posting...' : <><Send className="h-4 w-4 mr-2" />Post Reply</>}</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
